import { motion } from "framer-motion";
import { ArrowRight, Heart, Lightbulb, Smile } from "lucide-react";
import { Link } from "react-router-dom";
import { Eyebrow, Reveal } from "@/components/Reveal";

const TRAITS = [
  { icon: Lightbulb, label: "Curious about everything" },
  { icon: Heart, label: "Kind to everyone" },
  { icon: Smile, label: "Proud of who he is" },
];

export default function Characters() {
  return (
    <section data-testid="characters-section" className="py-24 sm:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center mb-14">
          <Eyebrow className="mb-4">Meet the characters</Eyebrow>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut">
            Say hello to Bailey
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
          <Reveal>
            <div className="relative spotlight rounded-[2rem] border border-sand p-10 overflow-hidden">
              <motion.img
                src="/images/book-bailey-affirmations.jpg"
                alt="Bailey — the hero of the Learn With Bailey series"
                className="w-full max-w-xs mx-auto rounded-xl drop-shadow-[0_30px_40px_rgba(49,67,92,0.35)]"
                whileHover={{ scale: 1.04, rotate: 1.5 }}
                transition={{ type: "spring", stiffness: 180, damping: 16 }}
              />
              <motion.span
                initial={{ scale: 0, rotate: 14 }}
                whileInView={{ scale: 1, rotate: 6 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.3 }}
                className="absolute top-6 right-6 bg-sage text-cream font-display font-extrabold text-xs uppercase tracking-widest rounded-full px-4 py-2.5 shadow-lg"
              >
                The hero
              </motion.span>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <p className="text-base sm:text-lg text-walnutsoft leading-relaxed mb-4">
                Bailey is a bright happy little explorer who tackles childhood's biggest
                moments with curiosity courage and a very big smile — from the first day
                of school to the dentist's chair and trips across London.
              </p>
              <p className="text-sm sm:text-base text-walnutsoft leading-relaxed mb-8">
                Children see themselves in Bailey and that's the point: every adventure
                is written so young readers feel inspired encouraged and proud. And
                Bailey never adventures alone — family friends and friendly faces like
                Dr Dentist join him along the way.
              </p>
            </Reveal>
            <div className="flex flex-col gap-3 mb-9">
              {TRAITS.map((t, i) => (
                <Reveal key={t.label} delay={i * 0.08}>
                  <div
                    data-testid={`character-trait-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex items-center gap-3 rounded-2xl border border-sand bg-white/70 px-5 py-3.5"
                  >
                    <t.icon size={18} className="text-terracotta shrink-0" />
                    <span className="font-display font-semibold text-sm sm:text-base text-walnut">{t.label}</span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/books"
                data-testid="characters-explore-books"
                className="group inline-flex items-center gap-2 rounded-full bg-walnut text-cream pl-7 pr-6 py-4 font-display font-bold text-sm sm:text-base hover:bg-terracotta transition-colors"
              >
                Join Bailey's adventures
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
